export const SITE_NAME = "LAA Consulting";
// Hosting canonicalises www → apex with a 301. Every canonical, sitemap entry,
// JSON-LD @id, email CTA and the Resend webhook endpoint must use the apex, or
// they point at a URL that immediately redirects.
export const SITE_URL = "https://laaconsulting.com";
const TITLE_SUFFIX = ` | ${SITE_NAME}`;

type JsonLd = Record<string, unknown>;

export interface SeoOptions {
  /** Page title. The " | LAA Consulting" suffix is appended unless already present. */
  title: string;
  description: string;
  /** Path beginning with "/" — used to build the self-referencing canonical. */
  path: string;
  /** Absolute https URL. Omitted entirely when not supplied. */
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: JsonLd | JsonLd[];
  /** og:type. Defaults to "website". */
  type?: "website" | "article";
  /** ISO date, emitted as article:published_time when type is "article". */
  publishedTime?: string;
  /** ISO date, emitted as article:modified_time when type is "article". */
  modifiedTime?: string;
}

export interface HeadTag {
  [key: string]: unknown;
}

export function canonicalUrl(path: string): string {
  return `${SITE_URL}${path === "/" ? "/" : path.replace(/\/$/, "")}`;
}

/** BreadcrumbList for a page, given the trail after Home. */
export function breadcrumbJsonLd(trail: { name: string; path: string }[]) {
  const last = trail[trail.length - 1];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${canonicalUrl(last ? last.path : "/")}#breadcrumbs`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      ...trail.map((item, i) => ({
        "@type": "ListItem",
        position: i + 2,
        name: item.name,
        item: canonicalUrl(item.path),
      })),
    ],
  };
}

export function seo({
  title,
  description,
  path,
  ogImage,
  noindex,
  jsonLd,
  type = "website",
  publishedTime,
  modifiedTime,
}: SeoOptions) {
  const fullTitle = title.endsWith(TITLE_SUFFIX) ? title : `${title}${TITLE_SUFFIX}`;
  const url = canonicalUrl(path);

  const meta: HeadTag[] = [
    { title: fullTitle },
    { name: "description", content: description },
    { property: "og:title", content: fullTitle },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:type", content: type },
    { property: "og:site_name", content: "LAA Concierge Consulting" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: fullTitle },
    { name: "twitter:description", content: description },
  ];

  if (type === "article" && publishedTime) {
    meta.push({ property: "article:published_time", content: publishedTime });
  }
  if (type === "article" && modifiedTime) {
    meta.push({ property: "article:modified_time", content: modifiedTime });
  }

  if (ogImage) {
    meta.push({ property: "og:image", content: ogImage });
    meta.push({ name: "twitter:image", content: ogImage });
  }

  if (noindex) {
    meta.push({ name: "robots", content: "noindex, follow" });
  }

  const links: HeadTag[] = [{ rel: "canonical", href: url }];

  const scripts: HeadTag[] = [];
  if (jsonLd) {
    const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    for (const block of blocks) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(block),
      });
    }
  }

  return { meta, links, scripts };
}
